import React, { useEffect, useRef, useState } from 'react';
import { styled, Alert as MuiAlert, AlertTitle as MuiAlertTitle, Collapse as MuiCollapse, } from '@mui/material';
import clsx from 'clsx';
import omit from 'lodash-es/omit';
import { IconCloseOutline } from '../../internal/icons';
import { bannerClasses, getStyle } from './style';
import { getBannerIconNode } from './icons';
import { ActionButton } from '../../Shared/ActionButton';
import { useTranslation } from '../../hooks/useTranslation';
function getAlertSeverity(type) {
    if (['info', 'success', 'warn', 'error'].includes(type)) {
        if (type === 'warn') {
            return 'warning';
        }
        return type;
    }
    return 'info';
}
function getAriaLiveValue(props) {
    // aria-live
    if (props.open === undefined) {
        return 'off';
    }
    if (['success', 'warn', 'error'].includes(props.type)) {
        return 'assertive';
    }
    return 'polite';
}
const renderBannerAction = (props) => {
    const { variant, onOk, onCancel, okText, cancelText, okButtonProps, cancelButtonProps, reverseButtons, action, actionable, cancelLabel, okLabel, afterAction, } = props;
    if (action) {
        return React.createElement("div", { className: bannerClasses.actionWrapper }, action);
    }
    if (actionable) {
        const buttonList = [];
        if (variant !== 'middle') {
            buttonList.push(React.createElement(ActionButton, { key: 0, type: "secondary", "aria-label": cancelLabel, ...cancelButtonProps, className: clsx(bannerClasses.actionBtn, bannerClasses.cancelBtn), onClick: onCancel, afterAction: afterAction }, cancelText));
        }
        buttonList.push(React.createElement(ActionButton, { key: 1, "aria-label": okLabel, ...okButtonProps, className: clsx(bannerClasses.actionBtn, bannerClasses.okBtn), onClick: onOk, afterAction: afterAction }, okText));
        if (reverseButtons) {
            buttonList.reverse();
        }
        return (React.createElement("div", { className: bannerClasses.actionWrapper }, buttonList.map((Btn) => Btn)));
    }
    return null;
};
const renderCloseButton = (props) => {
    const { closable, onClose, closeButtonProps, closeLabel, afterAction } = props;
    if (closable) {
        return (React.createElement(ActionButton, { className: bannerClasses.closeBtn, onClick: onClose, variant: "icon", type: "tertiary", size: "small", "aria-label": closeLabel, ...closeButtonProps, afterAction: afterAction },
            React.createElement(IconCloseOutline, { size: 12, "aria-hidden": "true" })));
    }
    return null;
};
const getTypeClassName = (type) => {
    if (type) {
        return bannerClasses[type] ?? bannerClasses.info;
    }
    return bannerClasses.info;
};
const BannerBase = React.forwardRef((props, ref) => {
    const { t } = useTranslation('Shared');
    const { variant, open = true, className, sx, title, content, type, cancelText = t('cancel'), okText = t('ok'), closable, contentProps, titleProps, closeButtonProps, cancelButtonProps, okButtonProps, onClose, onCancel, onOk, actionable, reverseButtons, icon, action, autoFocus, inline = true, autoTriggerClose = false, bordered = false, ...restProps } = props;
    const refAlert = useRef(null);
    const refPreviousElem = useRef(null);
    const [internalOpen, setInternalOpen] = useState(open);
    useEffect(() => {
        setInternalOpen(!!open);
    }, [open]);
    useEffect(() => {
        if (autoFocus) {
            if (open) {
                refPreviousElem.current = document.activeElement;
            }
            else {
                if (refPreviousElem.current) {
                    refPreviousElem.current.focus();
                }
                refPreviousElem.current = null;
            }
        }
    }, [open, autoFocus]);
    useEffect(() => {
        /* istanbul ignore else */
        if (refAlert.current && open && autoFocus) {
            let firstAXElement = null;
            if (closable) {
                firstAXElement = refAlert.current.querySelector(`.${bannerClasses.closeBtn}`);
            }
            if (!firstAXElement && actionable) {
                firstAXElement = refAlert.current.querySelector(`.${bannerClasses.okBtn}`);
            }
            /* istanbul ignore else */
            if (firstAXElement) {
                firstAXElement.focus();
            }
        }
    }, [open, autoFocus, actionable, closable]);
    const afterAction = autoTriggerClose
        ? () => {
            setInternalOpen(false);
        }
        : undefined;
    return (React.createElement(MuiCollapse, { in: internalOpen, ref: ref },
        React.createElement(MuiAlert, { ref: refAlert, classes: {
                icon: bannerClasses.iconWrapper,
                message: bannerClasses.message,
            }, className: clsx([
                bannerClasses.root,
                getTypeClassName(type),
                {
                    [bannerClasses.middle]: variant === 'middle',
                    [bannerClasses.inline]: inline,
                    [bannerClasses.noIcon]: icon === null,
                    [bannerClasses.bordered]: bordered,
                    [bannerClasses.closable]: closable,
                },
                className,
            ]), icon: icon !== null ? (React.cloneElement(getBannerIconNode(props), {
                'aria-hidden': 'true',
            })) : (React.createElement("span", null)), variant: "outlined", severity: getAlertSeverity(type), sx: sx, "aria-live": getAriaLiveValue(props), 
            // aria-label={getAriaLabel(props)}
            autoFocus: true, ...restProps, action: null },
            React.createElement("div", { className: bannerClasses.context },
                title && (React.createElement(MuiAlertTitle, { ...titleProps, className: bannerClasses.title }, title)),
                React.createElement("div", { ...contentProps, className: bannerClasses.content }, content)),
            renderBannerAction({
                ...props,
                cancelText,
                okText,
                closable,
                cancelLabel: typeof cancelText === 'string' ? cancelText : t('cancel'),
                okLabel: typeof okText === 'string' ? okText : t('ok'),
                afterAction,
            }),
            React.createElement("div", { className: bannerClasses.closeWrapper }, renderCloseButton({
                ...props,
                closable,
                closeLabel: t('close'),
                afterAction,
            })))));
});
const StyledBanner = styled(BannerBase)((props) => {
    const { theme } = props;
    return getStyle(theme);
});
const defaultProps = {
    closable: true,
    type: 'info',
};
const RefBanner = React.forwardRef((props, ref) => (React.createElement(StyledBanner, { ...props, ref: ref })));
//  The exposed components must support the use of ref.
const Banner = React.forwardRef((props, ref) => {
    const { className, sx } = props;
    const internalProps = omit(props, ['style']);
    return (React.createElement(RefBanner, { ...defaultProps, ...internalProps, ref: ref, className: className, sx: sx }));
});
export default Banner;
